import {gql} from '@apollo/client';
import {DocumentNode} from 'graphql';
import * as PropTypes from 'prop-types';
import React, {useState} from 'react';

import {assert} from 'Asserts';
import {GridType} from 'constants/SampleConstants';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {GridCreationSchemaFieldSelectorsQuery} from 'notebook/structured-tables/__generated/GridCreationSchemaFieldSelectorsQuery';
import {SchemaFieldSelectorsDict} from 'notebook/structured-tables/hooks/useSelectorsById';
import {StructuredTableKind} from 'notebook/structured-tables/StructuredTableConstants';
import {STRUCTURED_TABLE_PLUGIN} from 'notebook/structured-tables/StructuredTablePlugin';
import {getSelectorIdsFromFields} from 'notebook/structured-tables/util/StructuredTableColumnUtil';
import {StructuredTableInsertionContextValue} from 'notebook/structured-tables/util/StructuredTableInsertionContext';
import BoxSchemaSelector from 'samples/BoxSchemaSelector';
import PlateSchemaSelector from 'samples/PlateSchemaSelector';
import {GridSchema} from 'samples/storables/StorableSchemaConstants';
import {SELECTOR_SHAPE} from 'schemas/SchemaFieldSelectorConstants';
import ModalButtonKey from 'test/lib/cypress/shared/ModalButtonKey';
import {ButtonStyle} from 'ui-library/buttons/Button';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import RadioButtonGroup from 'ui-library/inputs/RadioButtonGroup';
import Modal from 'ui-library/modals/Modal';
import {assertNever} from 'util/Types';

import {GridCreationTablePreview} from './GridCreationTablePreview';

function SchemaSelector({
  gridType,
  selectedSchema,
  onChange,
}: {
  gridType: GridType;
  selectedSchema: GridSchema | null;
  onChange: (selectedSchema: GridSchema) => void;
}): JSX.Element | null {
  const commonProps = {shouldShowNoSchemaOption: false, onChange};
  const selectedSchemaId = selectedSchema ? selectedSchema.id : null;

  switch (gridType) {
    case GridType.BOX:
      return <BoxSchemaSelector {...commonProps} selectedBoxSchemaId={selectedSchemaId} />;
    case GridType.PLATE:
      return <PlateSchemaSelector {...commonProps} selectedPlateSchemaId={selectedSchemaId} />;
    default:
      assertNever(gridType);
      return null;
  }
}

function modalTitleFor(gridType: GridType): string {
  switch (gridType) {
    case GridType.BOX:
      return 'Insert box creation table';
    case GridType.PLATE:
      return 'Insert plate creation table';
    default:
      assertNever(gridType);
      return '';
  }
}

export interface GridCreationTableInsertModalProps {
  gridType: GridType;
  onRequestSubmit: (submitArgs: {
    schema: GridSchema;
    shouldAutogenerateBarcode: boolean;
    requireCustomName: boolean;
    selectorsById: SchemaFieldSelectorsDict;
  }) => void;
  onRequestClose: () => void;
  selectorsById: SchemaFieldSelectorsDict | null;
  structuredTableInsertionContext: StructuredTableInsertionContextValue;
}

export const GRID_CREATION_SCHEMA_FIELD_SELECTORS_QUERY: DocumentNode = gql`
  query GridCreationSchemaFieldSelectorsQuery @skipAudit @engComponent(name: INVENTORY) {
    schemaFieldSelectors {
      ...SchemaFieldSelectorGraphQL
    }
  }
  ${getFragmentWrapper('SchemaFieldSelector').fragment}
`;

function QueryingGridCreationTableInsertModal(
  props: Omit<GridCreationTableInsertModalProps, 'selectorsById'>
): JSX.Element {
  return (
    <BenchlingQuery<GridCreationSchemaFieldSelectorsQuery>
      query={GRID_CREATION_SCHEMA_FIELD_SELECTORS_QUERY}
      renderLoading={() => <GridCreationTableInsertModal {...props} selectorsById={null} />}
    >
      {({schemaFieldSelectors}) => {
        let selectorsById: SchemaFieldSelectorsDict = {};
        selectorsById = schemaFieldSelectors.reduce((acc, selector) => {
          acc[selector.id] = selector;
          return acc;
        }, selectorsById);

        return <GridCreationTableInsertModal {...props} selectorsById={selectorsById} />;
      }}
    </BenchlingQuery>
  );
}

function GridCreationTableInsertModal({
  gridType,
  onRequestSubmit,
  onRequestClose,
  selectorsById,
  structuredTableInsertionContext,
}: GridCreationTableInsertModalProps): JSX.Element {
  const [selectedSchema, setSelectedSchema] = useState<GridSchema | null>(null);
  const [shouldAutogenerateBarcode, setShouldAutogenerateBarcode] = useState(true);
  const [requireCustomName, setRequireCustomName] = useState(false);

  const insertEnabled =
    selectedSchema != null &&
    selectorsById != null &&
    getSelectorIdsFromFields(selectedSchema.fields).every((selectorId) => selectorsById[selectorId] != null);

  let tablePreview: JSX.Element | null = null;
  if (selectedSchema != null) {
    const schemaWithEmptyColumnGroup = {...selectedSchema, columnGroup: null};
    const metadata = {
      shouldAutogenerateBarcode,
      requireCustomName,
      parentStorableIsGrid: false,
    };
    tablePreview = (
      <GridCreationTablePreview
        gridType={gridType}
        schema={schemaWithEmptyColumnGroup}
        adapter={STRUCTURED_TABLE_PLUGIN.get(
          gridType === GridType.PLATE ? StructuredTableKind.PLATE_CREATION : StructuredTableKind.BOX_CREATION
        )}
        cid="123"
        selectorsById={selectorsById}
        metadata={metadata}
        structuredTableInsertionContext={structuredTableInsertionContext}
      />
    );
  }

  return (
    <Modal
      title={modalTitleFor(gridType)}
      onRequestClose={onRequestClose}
      onRequestSubmit={() => {
        if (!(selectedSchema && selectorsById)) {
          assert(false, 'Insert should be disabled if no schema is selected or selectors are not loaded.');
          return;
        }
        onRequestSubmit({
          schema: selectedSchema,
          shouldAutogenerateBarcode,
          requireCustomName,
          selectorsById,
        });
      }}
      buttons={[
        {
          text: 'Insert',
          submit: true,
          style: ButtonStyle.SECONDARY,
          disabled: !insertEnabled,
          dataTestAttrs: {component: 'GridCreationTableInsertModal', key: ModalButtonKey.INSERT},
        },
      ]}
      className="mediocre" // Preview support, TableEditable's CSS is namespaced under mediocre
      dataTestAttrs={{component: 'GridCreationTableInsertModal', element: 'root'}}
    >
      <FieldLabel label="Type" />
      <SchemaSelector gridType={gridType} selectedSchema={selectedSchema} onChange={setSelectedSchema} />
      <div className="u-small-padding-top">
        <label>
          <FieldLabel label="Name" />
          <RadioButtonGroup
            title=""
            options={[
              {value: false, label: 'Autogenerated'},
              {value: true, label: 'Custom'},
            ]}
            selectedValue={requireCustomName}
            onChange={(value: boolean) => {
              setRequireCustomName(value);
            }}
          />
        </label>
      </div>
      <div className="u-small-padding-top">
        <label>
          <FieldLabel label="Barcode" />
          <RadioButtonGroup
            title=""
            options={[
              {value: true, label: 'Autogenerated'},
              {value: false, label: 'Custom'},
            ]}
            selectedValue={shouldAutogenerateBarcode}
            onChange={(value: boolean) => {
              setShouldAutogenerateBarcode(value);
            }}
          />
        </label>
      </div>
      {tablePreview}
    </Modal>
  );
}

GridCreationTableInsertModal.propTypes = {
  gridType: PropTypes.string.isRequired,
  onRequestSubmit: PropTypes.func.isRequired,
  onRequestClose: PropTypes.func.isRequired,
  selectorsById: PropTypes.objectOf(SELECTOR_SHAPE.isRequired),
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
} as any;

export default QueryingGridCreationTableInsertModal;
